'use client'

import { useEffect, useState, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Icons } from '@/components/ui/icons'
import {
  LiveKitRoom,
  VideoConference,
  RoomAudioRenderer,
} from '@livekit/components-react'
import '@livekit/components-styles'
import { createClient } from '@anam-ai/js-sdk'

interface RoomData {
  token: string
  roomName: string
  wsUrl: string
}

interface VideoCallInterfaceProps {
  roomData: RoomData
  onEndCall: () => void
  participantName: string
  dealerLocation: string
  anamSessionToken: string | null
}

export function VideoCallInterface({
  roomData,
  onEndCall,
  participantName,
  dealerLocation,
  anamSessionToken,
}: VideoCallInterfaceProps) {
  const [connected, setConnected] = useState(false)
  const [aiStatus, setAiStatus] = useState<'initializing' | 'connecting' | 'connected' | 'disconnected' | 'error' | 'unavailable'>('initializing')
  const [tsoRequested, setTsoRequested] = useState(false)
  const [facialAnalysis, setFacialAnalysis] = useState<string>('')
  const [dealerSpeaking, setDealerSpeaking] = useState(false)
  const anamClientRef = useRef<unknown>(null)
  const facialServiceRef = useRef<{
    stopAnalysis: () => void;
    getInterpretation: (analysis: unknown) => string;
    startAnalysis: (video: HTMLVideoElement, callback: (analysis: unknown) => void) => void;
  } | null>(null)
  const videoElementId = 'anam-ai-video'
  const dealerVideoRef = useRef<HTMLVideoElement | null>(null)

  // Function to send facial analysis to backend
  const sendFacialAnalysisToBackend = async (
    analysis: {
      dominantExpression: string;
      confidence: number;
      lipMovement?: { likelySpeaking: boolean };
      engagement: { attentionLevel: number };
    },
    interpretation: string
  ) => {
    try {
      const response = await fetch('/api/video-call/facial-analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roomName: roomData.roomName,
          dealerName: participantName,
          dealerLocation: dealerLocation,
          analysis: {
            expression: analysis.dominantExpression,
            confidence: analysis.confidence,
            speaking: analysis.lipMovement?.likelySpeaking || false,
            engagement: analysis.engagement.attentionLevel,
            interpretation,
          },
        }),
      });
      
      const data = await response.json();
      
      // Check if AI initiated TSO escalation
      if (data.aiEscalated) {
        console.log('🤖 AI Persona initiated TSO escalation:', data.escalationReason);
        setTsoRequested(true);
        // Optionally show a toast/notification to dealer
      }
    } catch (error) {
      console.error('Failed to send facial analysis:', error);
    }
  };

  // Initialize Anam AI Client
  useEffect(() => {
    let mounted = true
    let isInitializing = false

    const initAnamClient = async () => {
      if (!anamSessionToken) {
        console.warn('⚠️ No Anam session token provided')
        setAiStatus('unavailable')
        return
      }

      if (isInitializing || anamClientRef.current) {
        return
      }

      try {
        isInitializing = true
        console.log('🤖 Initializing Anam AI client...')
        console.log('📝 Session token (first 20 chars):', anamSessionToken.substring(0, 20) + '...')
        console.log('🎥 Target video element ID:', videoElementId)
        
        // Check if video element exists
        const videoElement = document.getElementById(videoElementId)
        console.log('📺 Video element found:', videoElement ? 'Yes' : 'No')
        
        setAiStatus('connecting')

        await new Promise(resolve => setTimeout(resolve, 500))

        if (!mounted) return

        console.log('🔌 Creating Anam client...')
        const client = createClient(anamSessionToken, {
          disableInputAudio: false,
          voiceDetection: {
            endOfSpeechSensitivity: 0.6,
          },
        })
        console.log('✅ Anam client created')

        if (!mounted) {
          client.stopStreaming()
          return
        }

        anamClientRef.current = client

        // Connection listeners
        // @ts-expect-error - Anam SDK has strict event types
        client.addListener('CONNECTION_ESTABLISHED', () => {
          console.log('✅ Anam AI connected')
          if (mounted) setAiStatus('connected')
        })

        // @ts-expect-error - Anam SDK has strict event types
        client.addListener('CONNECTION_CLOSED', (reason: string, details: unknown) => {
          console.log('❌ Anam AI disconnected', reason, details)
          if (mounted) setAiStatus('disconnected')
        })

        // @ts-expect-error - Anam SDK has strict event types
        client.addListener('SESSION_READY', (sessionId: string) => {
          console.log('✅ Anam session ready:', sessionId)
        })

        // @ts-expect-error - Anam SDK has strict event types
        client.addListener('MIC_PERMISSION_PENDING', () => {
          console.log('🎤 Requesting microphone permission...')
        })

        // @ts-expect-error - Anam SDK has strict event types
        client.addListener('MIC_PERMISSION_GRANTED', () => {
          console.log('✅ Microphone permission granted')
        })

        // @ts-expect-error - Anam SDK has strict event types
        client.addListener('MIC_PERMISSION_DENIED', (error: unknown) => {
          console.error('❌ Microphone permission denied:', error)
          if (mounted) setAiStatus('error')
        })

        // @ts-expect-error - Anam SDK has strict event types
        client.addListener('ERROR', (error: unknown) => {
          console.error('❌ Anam error:', error)
          if (mounted) setAiStatus('error')
        })

        // Start streaming to video element
        console.log('🎬 Starting video stream to element:', videoElementId)
        await client.streamToVideoElement(videoElementId)
        console.log('🎥 Anam video stream started')
      } catch (error: unknown) {
        console.error('❌ Error initializing Anam:', error)
        console.error('Error type:', typeof error)
        console.error('Error constructor:', error?.constructor?.name)
        if (error instanceof Error) {
          console.error('Error message:', error.message)
          console.error('Error stack:', error.stack)
        } else {
          console.error('Error value:', error)
        }
        const errorMessage = error instanceof Error ? error.message : String(error)
        if (errorMessage.includes('Concurrency limit')) {
          console.error('⚠️ Concurrency limit reached. Please close other Anam sessions.')
        }
        if (errorMessage.includes('session')) {
          console.error('⚠️ Session-related error. The token might be invalid or expired.')
        }
        if (mounted) setAiStatus('error')
      } finally {
        isInitializing = false
      }
    }

    if (anamSessionToken && !anamClientRef.current) {
      initAnamClient()
    }

    return () => {
      mounted = false
      if (anamClientRef.current && typeof anamClientRef.current === 'object' && 'stopStreaming' in anamClientRef.current) {
        try {
          (anamClientRef.current as { stopStreaming: () => void }).stopStreaming()
          anamClientRef.current = null
          console.log('🛑 Anam client stopped and cleared')
        } catch (e) {
          console.error('Error stopping Anam:', e)
        }
      }
      
      // Stop facial analysis
      if (facialServiceRef.current) {
        facialServiceRef.current.stopAnalysis()
        facialServiceRef.current = null
      }
    }
  }, [anamSessionToken])

  // Initialize facial analysis for dealer video
  useEffect(() => {
    // Dynamically import facial analysis (client-side only to avoid SSR issues)
    let mounted = true;
    
    const initFacialAnalysis = async () => {
      // Dynamic import to avoid SSR issues with face-api.js
      const { default: FacialAnalysisService } = await import('@/services/facialAnalysis');
      
      const videoElements = document.querySelectorAll('video');
      
      // Find the dealer's video (not the AI avatar)
      for (const video of Array.from(videoElements)) {
        if (video.id !== videoElementId && video.srcObject) {
          dealerVideoRef.current = video;
          
          if (!facialServiceRef.current && mounted) {
            const service = new FacialAnalysisService();
            facialServiceRef.current = service as unknown as typeof facialServiceRef.current;
            
            service.startAnalysis(video, (analysis: unknown) => {
              if (!mounted) return;
              
              const fullAnalysis = analysis as {
                dominantExpression: string;
                confidence: number;
                lipMovement?: { likelySpeaking: boolean };
                engagement: { attentionLevel: number };
                expressions: Record<string, number>;
              };
              
              // Update facial analysis state
              const interpretation = service.getInterpretation(analysis as never);
              setFacialAnalysis(interpretation);
              setDealerSpeaking(fullAnalysis.lipMovement?.likelySpeaking || false);
              
              // Send analysis to backend for AI context
              if (connected) {
                sendFacialAnalysisToBackend(fullAnalysis, interpretation);
              }
            });
            
            console.log('✅ Facial analysis started for dealer video');
          }
          break;
        }
      }
    };

    // Retry finding video element every second until found
    const interval = setInterval(() => {
      if (connected && !facialServiceRef.current) {
        initFacialAnalysis().catch(console.error);
      }
    }, 1000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [connected]);

  const handleEndCall = () => {
    if (anamClientRef.current && typeof anamClientRef.current === 'object' && 'stopStreaming' in anamClientRef.current) {
      try {
        (anamClientRef.current as { stopStreaming: () => void }).stopStreaming()
      } catch (e) {
        console.error('Error stopping Anam on end call:', e)
      }
    }
    onEndCall()
  }

  const getAiStatusText = () => {
    switch (aiStatus) {
      case 'connected': return '● Active'
      case 'connecting': return '○ Connecting...'
      case 'error': return '✕ Error'
      case 'unavailable': return '✕ Unavailable'
      default: return '○ Starting...'
    }
  }

  return (
    <div className="flex h-screen flex-col bg-gray-900">
      {/* Header */}
      <div className="border-b border-white/10 bg-black/50 backdrop-blur-sm">
        <div className="flex items-center justify-between px-6 py-4">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-white">
              <Icons.building className="h-5 w-5 text-brand-cornflower" />
              Adani Cement - Sales Connect
            </h2>
            <p className="text-sm text-gray-400">
              <strong>{participantName}</strong>
              {dealerLocation && <span className="ml-2">• {dealerLocation}</span>}
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {tsoRequested && (
              <div className="flex items-center gap-2 rounded-lg bg-amber-500/20 px-4 py-2 text-amber-400">
                <Icons.userPlus className="h-5 w-5" />
                <span className="text-sm font-medium">Senior Manager Joining Soon - AI Requested Human Support</span>
              </div>
            )}
            
            <Button
              onClick={handleEndCall}
              variant="destructive"
              size="lg"
              className="gap-2"
            >
              <Icons.circle className="h-4 w-4 fill-current" />
              End Call
            </Button>
          </div>
        </div>
      </div>

      {/* Video Area */}
      <div className="flex flex-1 overflow-hidden">
        <div className="flex flex-1 flex-col gap-4 p-4 lg:flex-row">
          {/* Anam AI Avatar Video */}
          {anamSessionToken && (
            <div className="relative flex-1 overflow-hidden rounded-lg bg-black">
              <video 
                id={videoElementId}
                autoPlay 
                playsInline
                className="h-full w-full object-cover"
              />
              <div className="absolute bottom-4 left-4 rounded-lg bg-black/70 px-3 py-2 backdrop-blur-sm">
                <div className="flex items-center gap-2">
                  <Icons.user className="h-4 w-4 text-brand-cornflower" />
                  <span className="text-sm font-medium text-white">Priya Sharma - AI Sales Expert</span>
                  <span className={`text-xs ${aiStatus === 'connected' ? 'text-green-400' : 'text-yellow-400'}`}>
                    {getAiStatusText()}
                  </span>
                </div>
              </div>
              {aiStatus === 'error' && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/80">
                  <div className="text-center">
                    <Icons.alertCircle className="mx-auto mb-2 h-12 w-12 text-red-400" />
                    <div className="text-lg font-semibold text-white">AI Connection Failed</div>
                    <div className="mt-1 text-sm text-gray-400">
                      Close other tabs/sessions and refresh
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* LiveKit Video Conference */}
          <div className="relative flex-1 overflow-hidden rounded-lg bg-black">
            <LiveKitRoom
              token={roomData.token}
              serverUrl={roomData.wsUrl}
              connect={true}
              onConnected={() => {
                console.log('✅ Connected to LiveKit room')
                setConnected(true)
              }}
              onDisconnected={() => {
                console.log('❌ Disconnected from LiveKit room')
                setConnected(false)
                handleEndCall()
              }}
              audio={true}
              video={true}
              className="h-full w-full"
            >
              <VideoConference />
              <RoomAudioRenderer />
              
              {!connected && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/80">
                  <div className="text-center">
                    <Icons.loader className="mx-auto mb-2 h-12 w-12 animate-spin text-brand-cornflower" />
                    <p className="text-white">Connecting to room...</p>
                  </div>
                </div>
              )}
            </LiveKitRoom>
          </div>
        </div>
      </div>

      {/* Status Footer */}
      <div className="border-t border-white/10 bg-black/50 backdrop-blur-sm px-6 py-4">
        <div className="flex flex-wrap items-center justify-between gap-4 text-sm">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-gray-400">
              <Icons.video className="h-4 w-4 text-brand-cornflower" />
              <span>LiveKit Integration</span>
            </div>
            <div className="flex items-center gap-2">
              <Icons.brain className="h-4 w-4 text-brand-cornflower" />
              <span className={aiStatus === 'connected' ? 'text-green-400' : 'text-gray-400'}>
                {aiStatus === 'connected' ? 'Priya Active (Hindi/English)' : 
                 aiStatus === 'connecting' ? 'Priya Connecting...' :
                 aiStatus === 'error' ? 'AI Unavailable' : 'Priya Starting...'}
              </span>
            </div>
            <div className="flex items-center gap-2 text-gray-400">
              <Icons.sparkles className="h-4 w-4 text-brand-cornflower" />
              <span>HD Video Call</span>
            </div>
            <div className="flex items-center gap-2 text-gray-400">
              <Icons.lock className="h-4 w-4 text-brand-cornflower" />
              <span>Secure & Private</span>
            </div>
          </div>
          
          {/* Facial Analysis Indicator */}
          {facialAnalysis && (
            <div className="flex items-center gap-2 rounded-lg bg-purple-500/10 px-3 py-1.5 text-purple-300">
              <Icons.eye className="h-4 w-4" />
              <span className="text-xs">
                {dealerSpeaking && <strong className="text-purple-200">🗣️ Speaking: </strong>}
                {facialAnalysis}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
